import { useState, useEffect, useRef } from "react";
import { useAuth } from "../useAuth";
import { T, ALL_LABELS } from "../lib/i18n";
import { friendlyError, isoToLocalInput, buildGroupCodes, findGroupCode, tooLongGroupNames, parseGroupList, sortGroups, diffGroups, openCodeSheet, joinUrl } from "../lib/helpers";
import { dbCreateSessionWithFreshIds, dbDeleteSession, dbToggleLock, dbUpdateSession, dbLoadSessionStats, dbCheckIfStarted, dbLoadAllResults, dbLoadUserCourses, dbUploadQuestionImages, dbPruneQuestionImages } from "../lib/db";
import { ImportTab } from "./ImportTab";
import { Trash2 } from "lucide-react";
import { LabelText, QRDisplay, StatusDot, QuestionImages, QuestionText } from "../components/common";
import { RichTextField } from "../components/RichTextField";
import { sessionStatus, statusMeta } from "../lib/status";

// ─────────────────────────────────────────
//  TRAT EDITOR (View/Edit)
// ─────────────────────────────────────────
function TRATEditor({ session, onClose, onSave, onRefresh, lang, G, css }) {
  const [answerKey, setAnswerKey] = useState(session.answerKey || []);
  const [questions, setQuestions] = useState(session.questions || []);
  const [numOptions, setNumOptions] = useState(session.numOptions || 4);
  const [isEditable, setIsEditable] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groupCount, setGroupCount] = useState(0);
  const [tab, setTab] = useState("overview");
  // Sant efter en omimport: då — och bara då — skrivs num_questions/num_options
  // om vid spar, och bildblobbar laddas upp. En vanlig redigering rör dem inte
  // (frågelistan kan vara tom på gamla tRAT:ar som bara har facit).
  const [reimported, setReimported] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [copied, setCopied] = useState("");
  const [showEditorQR, setShowEditorQR] = useState(false);
  const [saveToast, setSaveToast] = useState(false);
  const [sessionTitle, setSessionTitle] = useState(session.sessionTitle || "");
  const [scheduleMode, setScheduleMode] = useState(session.availableAt ? "scheduled" : "now");
  const [availableAt, setAvailableAt] = useState(isoToLocalInput(session.availableAt));
  const [sessionDuration, setSessionDuration] = useState(session.config?.session_duration || "");
  const [groupMode, setGroupMode] = useState(session.config?.predefined_groups?.length > 0 ? "predefined" : "free");
  const [predefinedGroupsText, setPredefinedGroupsText] = useState((session.config?.predefined_groups || []).join("\n"));
  // Sessionens config som den ligger i databasen just nu. Egen state (och inte
  // session.config) eftersom den inskickade propen är oförändrad efter ett
  // spar: utan detta visade gruppkoderna och kodbladet fortfarande den gamla
  // grupplistan tills läraren stängde och öppnade tRAT:en på nytt.
  const [savedConfig, setSavedConfig] = useState(session.config || {});
  // Senaste sparet som faktiskt ändrade grupperna — styr kvittensen och
  // knappen "skriv ut kodblad för de nya grupperna".
  const [groupSaveInfo, setGroupSaveInfo] = useState(null);
  const [groupGenCount, setGroupGenCount] = useState(8);
  const [groupGenPrefix, setGroupGenPrefix] = useState("Grupp");
  const [randomizeQuestions, setRandomizeQuestions] = useState(!!session.config?.randomize_questions);
  const [randomizeOptions, setRandomizeOptions] = useState(!!session.config?.randomize_options);
  const [selectedCourseInstanceId, setSelectedCourseInstanceId] = useState(session.courseInstanceId || null);
  const [myCourseInstances, setMyCourseInstances] = useState([]);
  const { user } = useAuth();
  const t = T[lang];
  const LABELS = ALL_LABELS.slice(0, numOptions);

  useEffect(() => { if (user) dbLoadUserCourses(user.id).then(setMyCourseInstances); }, [user]);

  useEffect(() => {
    async function checkIfStarted() {
      const hasStarted = await dbCheckIfStarted(session.sessionId);
      if (hasStarted) {
        const results = await dbLoadAllResults(session.sessionId);
        setGroupCount(results.length);
        setIsEditable(false);
      }
    }
    checkIfStarted();
  }, [session.sessionId]);

  // Byter ut hela frågesattet mot ett nyimporterat. Skrivs först vid Spara, så
  // ett felklick går att ångra genom att stänga utan att spara.
  function handleReimport(importedQuestions) {
    // Största alternativantalet i uppsättningen, samma regel som i wizarden:
    // tas det från första frågan blir ett senare "E"-svar omöjligt att välja.
    const numOpts = Math.max(2, Math.min(6,
      Math.max(...importedQuestions.map(q => Object.keys(q.options || {}).length))));
    setNumOptions(numOpts);
    setAnswerKey(importedQuestions.map(q => q.correctAnswer));
    setQuestions(importedQuestions);
    setExpandedQuestions({});
    setReimported(true);
    setTab("questions");
  }

  // "Grupp 1 … Grupp 16" i ett klick, samma generator som i wizarden. Lägger
  // till efter det som redan står i rutan och hoppar över namn som redan
  // finns, så en grupp som är igång aldrig får en ny kod av misstag.
  function generateGroupNames() {
    const n = Math.max(1, Math.min(60, parseInt(groupGenCount) || 0));
    const prefix = groupGenPrefix.trim();
    const existing = parseGroupList(predefinedGroupsText);
    const seen = new Set(existing.map(l => l.toLowerCase()));
    const added = [];
    for (let i = 1; i <= n; i++) {
      const name = prefix ? `${prefix} ${i}` : String(i);
      if (seen.has(name.toLowerCase())) continue;
      seen.add(name.toLowerCase());
      added.push(name);
    }
    setPredefinedGroupsText(sortGroups([...existing, ...added]).join("\n"));
  }

  const [saveError, setSaveError] = useState("");

  // Grupplistan i rutan jämfört med den som ligger sparad: vad som tillkommit
  // (får en ny kod vid spar) och vad som försvinner.
  const savedGroups = savedConfig.predefined_groups || [];
  const savedCodes = savedConfig.group_codes || {};
  // Naturlig ordning redan i förhandsvisningen, så läraren ser var en
  // tillagd grupp hamnar — och sparas i samma ordning som den skrivs ut.
  const draftGroups = groupMode === "predefined" ? sortGroups(parseGroupList(predefinedGroupsText)) : [];
  const groupDiff = diffGroups(savedGroups, draftGroups);
  const groupsDirty = groupDiff.added.length > 0 || groupDiff.removed.length > 0;

  async function handleSave() {
    setSaveError("");
    if (groupMode === "predefined" && draftGroups.length === 0) {
      setSaveError(t.groupsRequired); setTab("settings"); return;
    }
    const tooLong = tooLongGroupNames(draftGroups);
    if (tooLong.length > 0) {
      setSaveError(t.groupNameTooLong(tooLong[0])); setTab("settings"); return;
    }
    setSaving(true);
    // Importerade figurer är blobbar tills de fått en plats i Storage; utan
    // uppladdning hamnar de som tomma objekt i JSON-kolumnen.
    let nextQuestions = questions;
    if (reimported && questions.some(q => q?.images?.length)) {
      nextQuestions = await dbUploadQuestionImages(session.sessionId, questions);
      setQuestions(nextQuestions);
    }
    // Grupper som står kvar behåller sin kod, så redan utdelade kodblad
    // fortsätter gälla; bara nya gruppnamn lottas en ny kod.
    const nextConfig = {
      ...savedConfig,
      ...(parseInt(sessionDuration) > 0 ? { session_duration: parseInt(sessionDuration) } : { session_duration: null }),
      predefined_groups: draftGroups,
      group_codes: draftGroups.length > 0 ? buildGroupCodes(draftGroups, savedCodes) : {},
      randomize_questions: randomizeQuestions,
      randomize_options: randomizeOptions,
    };
    const success = await dbUpdateSession(session.sessionId, {
      answer_key: answerKey,
      questions: nextQuestions,
      ...(reimported ? { num_questions: nextQuestions.length, num_options: numOptions } : {}),
      session_title: sessionTitle.trim() || null,
      course_instance_id: selectedCourseInstanceId || null,
      available_at: scheduleMode === "scheduled" && availableAt ? new Date(availableAt).toISOString() : null,
      config: nextConfig,
    });
    if (success && reimported) {
      await dbPruneQuestionImages(session.sessionId, nextQuestions);
      setReimported(false);
    }
    setSaving(false);
    if (success) {
      setSaveToast(true);
      setTimeout(() => setSaveToast(false), 2500);
      setSavedConfig(nextConfig);
      // Rutan visar samma ordning som det sparade — och som kodbladet.
      if (groupMode === "predefined") setPredefinedGroupsText(draftGroups.join("\n"));
      // Ändrade grupper: låt redigeraren stå kvar så läraren ser de nya
      // koderna och kan skriva ut kodbladet direkt. Listan bakom uppdateras
      // ändå via onRefresh. Övriga spar stänger som förut.
      const keepOpen = groupsDirty && draftGroups.length > 0;
      if (groupsDirty) setGroupSaveInfo(groupDiff);
      if (keepOpen) { setTab("settings"); onRefresh?.(); }
      else onSave();
    } else {
      // Nekat spar (utgången inloggning, RLS, nätverk): dialogen står kvar med
      // allt inskrivet, så läraren kan försöka igen i stället för att tro att
      // ändringen gick igenom.
      setSaveError(t.saveFailed);
    }
  }

  function setQ(qi, field, val) {
    setQuestions(prev => {
      const next = [...prev];
      next[qi] = { ...next[qi], [field]: val };
      return next;
    });
  }

  function copy(text, id) {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 2000);
  }

  // Kodbladet följer den redigerade titeln men den sparade koduppsättningen:
  // det som skrivs ut ska vara det grupperna faktiskt kan logga in med.
  const printCodeSheet = codes => openCodeSheet(sessionTitle.trim() || session.sessionTitle, session.sessionId, codes);
  const codeCard = { background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "10px 14px" };
  const printBtn = { background: G.accent + "18", border: `1px solid ${G.accent}44`, borderRadius: 8, color: G.accent, fontSize: 13, fontWeight: 600, padding: "7px 14px", cursor: "pointer", whiteSpace: "nowrap" };
  const printBtnNew = { ...printBtn, background: G.gold + "18", border: `1px solid ${G.gold}44`, color: G.gold };
  const dimmed = { opacity: 0.45, cursor: "not-allowed" };

  const TabBtn = ({ id, label }) => (
    <button 
      onClick={() => setTab(id)}
      style={{ 
        background: "transparent",
        color: tab === id ? G.text : G.muted,
        border: "none",
        borderBottom: tab === id ? `3px solid ${G.gold}` : "3px solid transparent",
        padding: "10px 20px", fontSize: 14,
        fontWeight: tab === id ? 700 : 500, 
        cursor: "pointer",
        transition: "all 0.2s",
        marginBottom: -2
      }}
      onMouseEnter={(e) => {
        if (tab !== id) {
          e.target.style.color = G.text;
          e.target.style.background = G.surface2;
        }
      }}
      onMouseLeave={(e) => {
        if (tab !== id) {
          e.target.style.color = G.muted;
          e.target.style.background = "transparent";
        }
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.8)", zIndex: 100, overflow: "auto", padding: "20px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", background: G.surface, borderRadius: 16, padding: "24px 32px", maxHeight: "90vh", overflow: "auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ ...css.heading, fontSize: 24, margin: 0 }}>
            {session.sessionTitle || `tRAT ${session.sessionId}`}
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: G.muted, fontSize: 24, cursor: "pointer" }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 28, borderBottom: `2px solid ${G.border}`, flexWrap: "wrap" }}>
          <TabBtn id="settings" label={t.settingsTab} />
          <TabBtn id="overview" label={t.tabOverview} />
          <TabBtn id="answerkey" label={t.tabAnswerKey} />
          <TabBtn id="questions" label={t.tabQuestions} />
          {isEditable && <TabBtn id="import" label={t.tabReimport} />}
        </div>

        {/* Lock Warning */}
        {!isEditable && tab !== "overview" && (
          <div style={{ background: G.red + "18", border: `2px solid ${G.red}44`, borderRadius: 12, padding: "14px 18px", marginBottom: 20 }}>
            <div style={{ fontSize: 14, color: G.red, fontWeight: 600 }}>
              {lang === 'sv' ? '🔒 Kan inte redigeras' : '🔒 Cannot be edited'}
            </div>
            <div style={{ fontSize: 14, color: G.text, marginTop: 4 }}>
              {lang === 'sv' 
                ? `${groupCount} ${t.groupsStarted}. Facit kan inte ändras.`
                : `${groupCount} ${t.groupsStarted}. Answer key cannot be changed.`
              }
            </div>
          </div>
        )}

        {/* Osparad omimport — syns på alla flikar utom import-fliken själv */}
        {reimported && tab !== "import" && (
          <div style={{ background: G.gold + "14", border: `1px solid ${G.gold}44`, borderRadius: 12, padding: "12px 18px", marginBottom: 20, fontSize: 14, color: G.text }}>
            {t.reimportPending}
          </div>
        )}

        {/* SETTINGS TAB */}
        {tab === "settings" && (
          <div>
            {/* Session Title */}
            <div style={{ marginBottom: 24 }}>
              <label style={css.label}><LabelText text={t.sessionTitle} /></label>
              <input type="text" style={css.input} placeholder={t.sessionTitlePlaceholder} value={sessionTitle} onChange={e => setSessionTitle(e.target.value)} />
            </div>
            {/* Course — assign/change the course this tRAT belongs to. Also the
                repair path for older tRATs created without a course. */}
            {myCourseInstances.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <label style={css.label}>{lang === "sv" ? "Kurs" : "Course"}</label>
                <select
                  style={css.input}
                  value={selectedCourseInstanceId || ""}
                  onChange={e => setSelectedCourseInstanceId(e.target.value || null)}
                >
                  <option value="">{lang === "sv" ? "— Ingen kurs —" : "— No course —"}</option>
                  {myCourseInstances.map(inst => (
                    <option key={inst.id} value={inst.id}>
                      {[inst.courses?.name, inst.name].filter(Boolean).join(" · ")}{inst.term ? " " + inst.term : ""}{inst.year ? " " + inst.year : ""}
                    </option>
                  ))}
                </select>
                {!selectedCourseInstanceId && (
                  <p style={{ color: G.gold, fontSize: 13, marginTop: 6 }}>
                    {lang === "sv" ? "Denna tRAT saknar kurs — välj en så den syns på kurssidan." : "This tRAT has no course — pick one so it shows on the course page."}
                  </p>
                )}
              </div>
            )}
            {/* Schedule Options */}
            <div style={{ marginBottom: 24 }}>
              <label style={css.label}>{t.sessionSchedule}</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                <button onClick={() => setScheduleMode("now")} style={{ 
                  background: scheduleMode === "now" ? G.surface2 : "transparent", 
                  color: scheduleMode === "now" ? G.text : G.muted, 
                  border: `1.5px solid ${scheduleMode === "now" ? G.gold : G.border}`, 
                  borderRadius: 8, padding: "10px 12px", fontSize: 14, 
                  fontWeight: scheduleMode === "now" ? 600 : 400, 
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}>
                  {t.makeAvailableNow}
                </button>
                <button onClick={() => setScheduleMode("scheduled")} style={{ 
                  background: scheduleMode === "scheduled" ? G.surface2 : "transparent", 
                  color: scheduleMode === "scheduled" ? G.text : G.muted, 
                  border: `1.5px solid ${scheduleMode === "scheduled" ? G.gold : G.border}`, 
                  borderRadius: 8, padding: "10px 12px", fontSize: 14, 
                  fontWeight: scheduleMode === "scheduled" ? 600 : 400, 
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}>
                  {t.makeAvailableAt}
                </button>
              </div>
              {scheduleMode === "scheduled" && (
                <input type="datetime-local" style={css.input} value={availableAt} onChange={e => setAvailableAt(e.target.value)} />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
                <label style={{ ...css.label, marginBottom: 0, whiteSpace: "nowrap" }}>{t.sessionDurationLabel}</label>
                <input type="number" min={1} max={180} style={{ ...css.input, width: 80 }} placeholder="—" value={sessionDuration} onChange={e => setSessionDuration(e.target.value)} />
                <span style={{ fontSize: 13, color: G.muted }}>{t.sessionDurationHint}</span>
              </div>
            </div>
            {/* Group registration mode */}
            <div style={{ marginBottom: 24 }}>
              <label style={css.label}>{t.predefinedGroups}</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                <button onClick={() => setGroupMode("free")} style={{ 
                  background: groupMode === "free" ? G.surface2 : "transparent", 
                  color: groupMode === "free" ? G.text : G.muted, 
                  border: `1.5px solid ${groupMode === "free" ? G.gold : G.border}`, 
                  borderRadius: 8, padding: "10px 12px", fontSize: 14, 
                  fontWeight: groupMode === "free" ? 600 : 400, 
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}>
                  {t.groupModeFree}
                </button>
                <button onClick={() => setGroupMode("predefined")} style={{ 
                  background: groupMode === "predefined" ? G.surface2 : "transparent", 
                  color: groupMode === "predefined" ? G.text : G.muted, 
                  border: `1.5px solid ${groupMode === "predefined" ? G.gold : G.border}`, 
                  borderRadius: 8, padding: "10px 12px", fontSize: 14, 
                  fontWeight: groupMode === "predefined" ? 600 : 400, 
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}>
                  {t.groupModePredefined}
                </button>
              </div>
              {groupMode === "predefined" && (
                <div>
                  <p style={{ color: G.muted, fontSize: 14, marginBottom: 10 }}>{t.predefinedGroupsHint}</p>
                  {/* Samma generator som i wizarden — att knappa in 16 grupper
                      en och en är lika onödigt efter att tRAT:en är skapad.
                      Namn som redan finns hoppas över och behåller sin kod. */}
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    <div>
                      <label style={{ ...css.label, fontSize: 10 }}>{t.groupGenCount}</label>
                      <input type="number" min={1} max={60} style={{ ...css.input, width: 80 }} value={groupGenCount} onChange={e => setGroupGenCount(e.target.value)} />
                    </div>
                    <div style={{ flex: "1 1 140px", minWidth: 120 }}>
                      <label style={{ ...css.label, fontSize: 10 }}>{t.groupGenPrefix}</label>
                      <input type="text" style={css.input} value={groupGenPrefix} onChange={e => setGroupGenPrefix(e.target.value)} />
                    </div>
                    <button onClick={generateGroupNames} style={{ background: G.gold + "18", border: `1px solid ${G.gold}55`, borderRadius: 8, color: G.gold, fontSize: 13, fontWeight: 600, padding: "9px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap" }}>
                      {t.groupGenAdd}
                    </button>
                  </div>
                  <textarea style={{ ...css.textarea, minHeight: 120, fontFamily: "'DM Mono',monospace", fontSize: 14 }} placeholder={t.predefinedGroupsPlaceholder} value={predefinedGroupsText} onChange={e => setPredefinedGroupsText(e.target.value)} />

                  {/* Gruppkoderna intill listan de hör ihop med: vad som redan
                      gäller, vad ett spar skulle ändra, och kodbladet att
                      skriva ut när ändringen är sparad. */}
                  {(draftGroups.length > 0 || groupDiff.removed.length > 0) && (
                    <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "16px 18px", marginTop: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                        <div>
                          <div style={{ ...css.label, marginBottom: 4 }}>{t.groupCodesTitle}</div>
                          <div style={{ fontSize: 13, color: G.muted }}>{t.groupCodesDesc}</div>
                        </div>
                        {/* Utskrift är avstängd så länge rutan och det sparade
                            skiljer sig: ett kodblad som saknar den grupp man
                            just skrivit in vore värre än ingen knapp alls. */}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {Object.keys(savedCodes).length > 0 && (
                            <button
                              onClick={() => printCodeSheet(savedCodes)}
                              disabled={groupsDirty}
                              title={groupsDirty ? t.groupCodesPending : undefined}
                              style={{ ...printBtn, ...(groupsDirty ? dimmed : {}) }}
                            >
                              {t.downloadCodeSheet}
                            </button>
                          )}
                          {groupSaveInfo?.added.length > 0 && (
                            <button
                              onClick={() => printCodeSheet(Object.fromEntries(groupSaveInfo.added.filter(g => savedCodes[g]).map(g => [g, savedCodes[g]])))}
                              disabled={groupsDirty}
                              title={groupsDirty ? t.groupCodesPending : undefined}
                              style={{ ...printBtnNew, ...(groupsDirty ? dimmed : {}) }}
                            >
                              {t.downloadCodeSheetNew} ({groupSaveInfo.added.length})
                            </button>
                          )}
                        </div>
                      </div>
                      {groupsDirty ? (
                        <div style={{ fontSize: 13, color: G.gold, marginBottom: 12 }}>{t.groupCodesPending}</div>
                      ) : groupSaveInfo && (
                        <div style={{ fontSize: 13, color: G.correct, marginBottom: 12 }}>{t.groupCodesSaved}</div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                        {draftGroups.map(g => (
                          <div key={g} style={codeCard}>
                            <div title={g} style={{ fontSize: 12, color: G.muted, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g}</div>
                            {findGroupCode(savedCodes, g) ? (
                              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 20, fontWeight: 700, color: G.gold, letterSpacing: "0.15em" }}>{findGroupCode(savedCodes, g)}</div>
                            ) : (
                              <div style={{ fontSize: 12, color: G.accent, fontWeight: 600 }}>{t.groupCodesNewBadge}</div>
                            )}
                          </div>
                        ))}
                        {groupDiff.removed.map(g => (
                          <div key={`removed-${g}`} style={{ ...codeCard, opacity: 0.55 }}>
                            <div title={g} style={{ fontSize: 12, color: G.muted, marginBottom: 4, textDecoration: "line-through", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g}</div>
                            <div style={{ fontSize: 12, color: G.red, fontWeight: 600 }}>{t.groupCodesRemovedBadge}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Slumpad ordning — samma inställning som i wizarden. Grupper som
                redan anslutit behåller sin ordning; den lottas vid anslutning. */}
            <div style={{ marginBottom: 24 }}>
              <label style={css.label}>{t.randomizeLabel}</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                {[
                  { on: randomizeQuestions, set: setRandomizeQuestions, label: t.randomizeQuestions, desc: t.randomizeQuestionsDesc },
                  { on: randomizeOptions, set: setRandomizeOptions, label: t.randomizeOptions, desc: t.randomizeOptionsDesc },
                ].map((o, i) => (
                  <label key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, background: o.on ? G.gold + "12" : G.bg, border: `1.5px solid ${o.on ? G.gold + "66" : G.border}`, borderRadius: 10, padding: "10px 14px", cursor: "pointer", fontSize: 14, color: G.text, transition: "all 0.15s" }}>
                    <input
                      type="checkbox"
                      checked={o.on}
                      onChange={e => o.set(e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: G.gold, flexShrink: 0, marginTop: 2 }}
                    />
                    <span>
                      <span style={{ fontWeight: 500 }}>{o.label}</span>
                      <span style={{ display: "block", fontSize: 12, color: G.muted, lineHeight: 1.5, marginTop: 2 }}>{o.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
              <p style={{ color: G.muted, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{t.randomizeHint}</p>
            </div>
          </div>
        )}

        {/* OVERVIEW TAB */}
        {tab === "overview" && (
          <div>
            {/* Codes */}
            {/* Student code */}
            <div style={{ background: G.accent + "0e", border: `1px solid ${G.accent}33`, borderRadius: 12, padding: "20px 24px", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: G.accent }} />
                <span style={{ ...css.label, marginBottom: 0, color: G.accent, fontSize: 12 }}>{lang === 'sv' ? 'För studenter' : 'For students'}</span>
              </div>
              <label style={{ ...css.label, fontSize: 12, marginBottom: 6 }}><LabelText text={t.tratCode} /></label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 28, color: G.gold, letterSpacing: "0.15em", fontWeight: 600 }}>{session.sessionId}</div>
                <button onClick={() => copy(session.sessionId, "session")} style={{ background: copied === "session" ? G.accent + "22" : G.surface2, border: `1px solid ${copied === "session" ? G.accent : G.border}`, borderRadius: 8, color: copied === "session" ? G.accent : G.text, fontSize: 13, padding: "6px 12px", cursor: "pointer" }}>
                  {copied === "session" ? `✓ ${t.copied}` : t.copy}
                </button>
                <button onClick={() => setShowEditorQR(!showEditorQR)} style={{ background: showEditorQR ? G.gold + "22" : G.surface2, border: `1px solid ${showEditorQR ? G.gold : G.border}`, borderRadius: 8, color: showEditorQR ? G.gold : G.text, fontSize: 13, padding: "6px 12px", cursor: "pointer" }}>{t.qrCode}</button>
              </div>
              {showEditorQR && <QRDisplay text={joinUrl(session.sessionId)} G={G} size={200} />}
              <div style={{ fontSize: 13, color: G.accent, marginTop: 8 }}>{lang === 'sv' ? 'Studenterna skriver in denna kod för att gå med.' : 'Students enter this code to join.'}</div>
            </div>

            {/* Group codes — only shown when predefined groups with codes exist */}
            {Object.keys(savedCodes).length > 0 && (
              <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "20px 24px", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ ...css.label, marginBottom: 4 }}>{t.groupCodesTitle}</div>
                    <div style={{ fontSize: 13, color: G.muted }}>{t.groupCodesDesc}</div>
                  </div>
                  <button
                    onClick={() => printCodeSheet(savedCodes)}
                    style={{ background: G.accent + "18", border: `1px solid ${G.accent}44`, borderRadius: 8, color: G.accent, fontSize: 13, fontWeight: 600, padding: "7px 14px", cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    {t.downloadCodeSheet}
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
                  {Object.entries(savedCodes).map(([group, code]) => (
                    <div key={group} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "10px 14px" }}>
                      <div style={{ fontSize: 12, color: G.muted, marginBottom: 4 }}>{group}</div>
                      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 22, fontWeight: 700, color: G.gold, letterSpacing: "0.15em" }}>{code}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Teacher dashboard */}
            <div style={{ background: G.gold + "0a", border: `1px solid ${G.gold}33`, borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: G.gold }} />
                <span style={{ ...css.label, marginBottom: 0, color: G.gold, fontSize: 12 }}>{lang === 'sv' ? 'För lärare' : 'For teachers'}</span>
              </div>
              <label style={{ ...css.label, fontSize: 12, marginBottom: 6 }}>{t.dashboardLink}</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button onClick={() => window.open(`/?dash=${session.dashId}`, '_blank')} style={{ background: G.gold + "22", border: `1px solid ${G.gold}44`, borderRadius: 8, color: G.gold, fontSize: 13, padding: "6px 12px", cursor: "pointer", fontWeight: 600 }}>
                  {t.openDashboard}
                </button>
              </div>
            </div>

            {/* Leaderboard Link */}
            {session.leaderboardId && (
              <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
                <label style={{ ...css.label, fontSize: 12, marginBottom: 6 }}>{t.leaderboardLink}</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color: G.text }}>{session.leaderboardId}</div>
                  <button onClick={() => window.open(`/?lb=${session.leaderboardId}`, '_blank')} style={{ background: G.gold + "22", border: `1px solid ${G.gold}44`, borderRadius: 8, color: G.gold, fontSize: 13, padding: "6px 12px", cursor: "pointer", fontWeight: 600 }}>
                    {t.openLeaderboard}
                  </button>
                </div>
              </div>
            )}

            {/* Status */}
            <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "16px 20px" }}>
              <div style={{ fontSize: 14, color: G.muted, marginBottom: 8 }}>Status</div>
              <div style={{ fontSize: 16, color: groupCount > 0 ? G.gold : G.text, fontWeight: 500 }}>
                {groupCount > 0 
                  ? `${groupCount} ${t.groupsStarted}`
                  : t.noGroupsStarted
                }
              </div>
              {session.availableAt && (
                <div style={{ fontSize: 14, color: G.muted, marginTop: 8 }}>
                  {t.scheduledFor}: {new Date(session.availableAt).toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-US')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ANSWER KEY TAB */}
        {tab === "answerkey" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
              {answerKey.map((v, qi) => (
                <div key={qi} style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 14, color: G.text, marginBottom: 8, fontWeight: 600 }}>Q{qi + 1}</div>
                  {isEditable ? (
                    <select 
                      style={{ background: "transparent", border: "none", borderBottom: `1px solid ${G.goldDim}`, color: G.gold, fontFamily: "'DM Mono',monospace", fontSize: 17, fontWeight: 500, textAlign: "center", outline: "none", cursor: "pointer", width: "100%", padding: "2px 0" }}
                      value={v} 
                      onChange={e => { const n = [...answerKey]; n[qi] = parseInt(e.target.value); setAnswerKey(n); }}
                    >
                      {LABELS.map((l, i) => <option key={i} value={i} style={{ background: G.surface }}>{l}</option>)}
                    </select>
                  ) : (
                    <div style={{ color: G.gold, fontFamily: "'DM Mono',monospace", fontSize: 17, fontWeight: 500 }}>{LABELS[v]}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* QUESTIONS TAB */}
        {tab === "questions" && (
          <div>
            {questions.length > 0 ? (
              questions.map((q, qi) => (
                <div key={qi} style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setExpandedQuestions(p => ({ ...p, [qi]: !p[qi] }))}>
                    <div style={{ fontSize: 14, color: G.gold, fontWeight: 600 }}>Q{qi + 1} - {t.answerKey}: {LABELS[answerKey[qi]]}</div>
                    <div style={{ fontSize: 20, color: G.muted }}>{expandedQuestions[qi] ? "▲" : "▼"}</div>
                  </div>
                  
                  {expandedQuestions[qi] && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ ...css.label, fontSize: 10, marginBottom: 4 }}>{t.questionText}</label>
                        {isEditable ? (
                          <RichTextField value={q?.text || ""} onChange={v => setQ(qi, "text", v)} ariaLabel={`Q${qi + 1} ${t.questionText}`} minHeight={60} G={G} css={css} lang={lang} />
                        ) : (
                          q?.text?.trim()
                            ? <QuestionText text={q.text} style={{ fontSize: 14, color: G.text, padding: "8px 0" }} />
                            : <div style={{ fontSize: 14, color: G.text, padding: "8px 0" }}>—</div>
                        )}
                      </div>
                      <QuestionImages images={q?.images} G={G} lang={lang} maxHeight={260} />
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ ...css.label, fontSize: 10, marginBottom: 6, color: G.goldDim }}>{lang === 'sv' ? 'Svarsalternativ' : 'Answer options'}{isEditable && <span style={{ color: G.muted, fontWeight: 400, marginLeft: 6 }}>({lang === 'sv' ? 'klicka på bokstav för att sätta rätt svar' : 'click letter to set correct answer'})</span>}</label>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {LABELS.map(lbl => {
                            const isCorrect = lbl === LABELS[answerKey[qi]];
                            return (
                              <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span
                                  onClick={() => { if (isEditable) { setAnswerKey(prev => { const n = [...prev]; n[qi] = LABELS.indexOf(lbl); return n; }); }}}
                                  title={isEditable ? (lang === 'sv' ? 'Markera som rätt svar' : 'Mark as correct answer') : undefined}
                                  style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: 600, minWidth: 24, textAlign: "center", padding: "2px 5px", borderRadius: 6, cursor: isEditable ? "pointer" : "default", color: isCorrect ? G.correct : G.goldDim, background: isCorrect ? G.correct + "20" : isEditable ? G.surface : "transparent", transition: "all 0.15s" }}
                                >
                                  {lbl}{isCorrect ? " ✓" : ""}
                                </span>
                                {isEditable ? (
                                  <input style={{ ...css.input, fontSize: 14, padding: "7px 10px", flex: 1 }} value={q?.options?.[lbl] || ""} onChange={e => setQ(qi, "options", { ...(q?.options || {}), [lbl]: e.target.value })} />
                                ) : (
                                  <div style={{ fontSize: 14, color: isCorrect ? G.correct : G.text, flex: 1, fontWeight: isCorrect ? 500 : 400 }}>{q?.options?.[lbl] || "—"}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <label style={{ ...css.label, fontSize: 10, marginBottom: 4, color: G.goldDim }}>{t.rationaleText}</label>
                        {isEditable ? (
                          <RichTextField value={q?.rationale || ""} onChange={v => setQ(qi, "rationale", v)} ariaLabel={`Q${qi + 1} ${t.rationaleText}`} minHeight={60} G={G} css={css} lang={lang} />
                        ) : (
                          q?.rationale?.trim()
                            ? <QuestionText text={q.rationale} style={{ fontSize: 14, color: G.text, padding: "8px 0" }} />
                            : <div style={{ fontSize: 14, color: G.text, padding: "8px 0" }}>—</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div style={{ textAlign: "center", color: G.muted, padding: "40px 20px" }}>
                {lang === 'sv' ? 'Inga frågor tillagda' : 'No questions added'}
              </div>
            )}
          </div>
        )}

        {/* RE-IMPORT TAB — byt frågesatt på en tRAT som ingen startat än */}
        {tab === "import" && isEditable && (
          <div>
            <div style={{ background: G.gold + "14", border: `1px solid ${G.gold}44`, borderRadius: 12, padding: "14px 18px", marginBottom: 24 }}>
              <div style={{ fontSize: 14, color: G.text }}>
                {reimported ? t.reimportPending : t.reimportWarning.replace("{n}", questions.length)}
              </div>
            </div>
            <ImportTab onImport={handleReimport} lang={lang} G={G} css={css} />
          </div>
        )}

        {saveError && (
          <div role="alert" style={{ background: G.red + "18", border: `1.5px solid ${G.red}55`, borderRadius: 12, padding: "12px 16px", marginTop: 16, fontSize: 14, color: G.text }}>{saveError}</div>
        )}
        {/* Actions */}
        <div style={{ display: "flex", gap: 12, marginTop: 28, paddingTop: 20, borderTop: `1px solid ${G.border}` }}>
          {(isEditable || tab === "settings") && tab !== "overview" && (
            <button onClick={handleSave} disabled={saving} style={{ ...css.btnPrimary, flex: 1 }}>
              {saving ? (lang === 'sv' ? 'Sparar...' : 'Saving...') : (lang === 'sv' ? 'Spara ändringar' : 'Save changes')}
            </button>
          )}
          <button onClick={onClose} style={{ ...css.btnSecondary, flex: ((isEditable || tab === "settings") && tab !== "overview") ? 0 : 1, minWidth: ((isEditable || tab === "settings") && tab !== "overview") ? 120 : 'auto' }}>
            {lang === 'sv' ? 'Stäng' : 'Close'}
          </button>
        </div>
      </div>
      {saveToast && (
        <>
        <style>{`@keyframes toastIn{from{opacity:0;transform:translateX(-50%) translateY(12px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`}</style>
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: G.surface, border: `1px solid ${G.border}`,
          borderRadius: 12, padding: "8px 20px", fontSize: 13, fontWeight: 500,
          color: G.text, fontFamily: "'DM Sans',sans-serif",
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)", zIndex: 200,
          animation: "toastIn 0.25s ease-out",
          whiteSpace: "nowrap",
        }}>
          {lang === "sv" ? "Ändringar sparade!" : "Changes saved!"}
        </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
//  TEACHER SESSION LIST
// ─────────────────────────────────────────
function TeacherSessionList({ sessions, onRefresh, onDashboard, lang, G, css, courseLabelById = {}, teacherNames = {}, appealCounts = {} }) {
  const [viewingSession, setViewingSession] = useState(null);
  const [sessionStats, setSessionStats] = useState({});
  const [toast, showToast] = useToast();
  const t = T[lang];
  const { user } = useAuth();

  useEffect(() => {
    sessions.forEach(s => {
      dbLoadSessionStats(s.sessionId).then(stats => {
        if (stats) setSessionStats(prev => ({ ...prev, [s.sessionId]: stats }));
      });
    });
  }, [sessions]);

  if (sessions.length === 0) {
    return (
      <div style={{ border: `1px dashed ${G.border}`, borderRadius: 8, padding: "20px 24px", fontSize: 13, color: G.muted, textAlign: "center" }}>
        {lang === "sv" ? "Inga tRAT:er ännu. Skapa din första ovan!" : "No tRATs yet. Create your first one above!"}
      </div>
    );
  }

  return (
    <>
      {viewingSession && (
        <TRATEditor
          session={viewingSession}
          onClose={() => setViewingSession(null)}
          onSave={() => { setViewingSession(null); onRefresh(); }}
          onRefresh={onRefresh}
          lang={lang}
          G={G}
          css={css}
        />
      )}
      <div style={{ border: `1px solid ${G.border}`, borderRadius: 10, background: G.surface }}>
      {sessions.map((s, i) => {
        const st = sessionStats[s.sessionId];
        const isLast = i === sessions.length - 1;
        const upcoming = s.availableAt && new Date(s.availableAt).getTime() > Date.now();
        // Shared status model (src/lib/status.js) — same words as Översikt,
        // kursvyn och dashboarden
        const meta = statusMeta(sessionStatus({
          locked: s.sessionLocked,
          availableAt: s.availableAt,
          activeGroups: st ? Math.max(0, (st.count || 0) - (st.doneCount || 0)) : 0,
          doneGroups: st?.doneCount || 0,
        }), lang, G);
        const dotColor = meta.dot;
        const statusLabel = meta.label;
        return (
        <div key={s.sessionId}
          className="trat-row"
          onClick={(e) => {
            if (e.target.closest('button') || e.target.closest('.no-click')) return;
            setViewingSession(s);
          }}
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
            padding: "13px 18px",
            borderBottom: !isLast ? `1px solid ${G.border}` : "none",
            cursor: "pointer",
            transition: "background 0.1s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = G.surface2}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: G.text, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.sessionTitle || `tRAT ${s.sessionId}`}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <StatusDot G={G} color={dotColor} label={statusLabel} />
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: G.muted, letterSpacing: "0.04em" }}>{s.sessionId}</span>
                {appealCounts[s.sessionId] > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: G.gold, background: G.gold + "18", border: `1px solid ${G.gold}44`, borderRadius: 5, padding: "1px 7px" }}>
                    {appealCounts[s.sessionId]} {appealCounts[s.sessionId] === 1 ? "appeal" : "appeals"}
                  </span>
                )}
                {user && s.teacherId && s.teacherId !== user.id && (
                  <span style={{ fontSize: 11, color: G.sub, background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 5, padding: "1px 7px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {teacherNames[s.sessionId] || (lang === "sv" ? "Kollega" : "Colleague")}
                  </span>
                )}
                {courseLabelById[s.courseInstanceId] && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: G.accent, background: G.accent + "14", border: `1px solid ${G.accent}33`, borderRadius: 5, padding: "1px 7px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={courseLabelById[s.courseInstanceId]}>
                    {courseLabelById[s.courseInstanceId]}
                  </span>
                )}
                <span style={{ fontSize: 12, color: G.muted }}>{s.numQ} {lang === "sv" ? "frågor" : "questions"}</span>
                <span style={{ fontSize: 12, color: G.muted }}>{new Date(s.createdAt).toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' })}</span>
                {st && (
                  <>
                    <StatusDot G={G} color={G.accent} label={`${st.doneCount} ${t.groupsCompleted2}`} />
                    {st.avgPct != null && <StatusDot G={G} color={G.gold} label={`Ø ${st.avgPct}%`} />}
                  </>
                )}
                {upcoming && (
                  <span style={{ fontSize: 12, color: G.gold }}>
                    {t.scheduledFor}: {new Date(s.availableAt).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </div>

            {/* Dashboard button and Three-dot menu */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
              <button
                onClick={(e) => { e.stopPropagation(); onDashboard ? onDashboard(s.dashId) : window.open(`/?dash=${s.dashId}`, '_blank'); }}
                style={{ background: "transparent", border: `1px solid ${G.border}`, borderRadius: 6, color: G.text, fontSize: 12, padding: "5px 11px", cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Dashboard →
              </button>
              
              <SessionActionsMenu
                session={s}
                onView={() => setViewingSession(s)}
                onChanged={onRefresh}
                notify={showToast}
                lang={lang}
                G={G}
              />
          </div>

        </div>
        );
      })}
    </div>
    <Toast toast={toast} G={G} />
    </>
  );
}

// ─────────────────────────────────────────
//  TOAST — kort bekräftelse längst ner
// ─────────────────────────────────────────
function useToast() {
  const [toast, setToast] = useState(null); // { text, isError }
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  function show(text, isError = false) {
    clearTimeout(timer.current);
    setToast({ text, isError });
    timer.current = setTimeout(() => setToast(null), 3000);
  }
  return [toast, show];
}

function Toast({ toast, G }) {
  if (!toast) return null;
  return (
    <div role="status" style={{
      position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
      background: G.surface, border: `1px solid ${toast.isError ? G.red + "66" : G.border}`,
      borderRadius: 12, padding: "10px 20px", fontSize: 13, fontWeight: 500,
      color: toast.isError ? G.red : G.text, fontFamily: "'DM Sans',sans-serif",
      boxShadow: "0 4px 16px rgba(0,0,0,0.25)", zIndex: 300,
      maxWidth: "calc(100vw - 32px)", textAlign: "center",
    }}>
      {toast.text}
    </div>
  );
}

// ─────────────────────────────────────────
//  SESSION ACTIONS MENU (⋮) — startsidan och kursvyn
// ─────────────────────────────────────────
// Radera visas bara för den som skapade tRAT:en: RLS tillåter bara ägaren
// att radera, och en kollegas försök gav förut "raderad" utan att något hände.
function SessionActionsMenu({ session, onView, onChanged, notify, lang, G }) {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Avsluta stänger tRAT:en för studenterna (de skickas till startsidan), så
  // det bekräftas först. Öppna igen gör ingen skada och går direkt.
  const [confirmLock, setConfirmLock] = useState(false);
  const { user } = useAuth();
  const t = T[lang];
  const sv = lang === "sv";
  const isOwner = !!user && session.teacherId === user.id;

  function close() { setOpen(false); setConfirmDelete(false); }

  async function handleDuplicate() {
    close();
    try {
      // Carry over the course link and the predefined groups (with FRESH codes
      // for the new run); drop the stale running timer and old schedule.
      // Slumpinställningarna följer med: kopian ska bete sig som originalet.
      const groups = session.config?.predefined_groups || [];
      const dur = session.config?.session_duration;
      const config = {
        ...(groups.length > 0 ? { predefined_groups: groups, group_codes: buildGroupCodes(groups, {}) } : {}),
        ...(dur ? { session_duration: dur } : {}),
        ...(session.config?.randomize_questions ? { randomize_questions: true } : {}),
        ...(session.config?.randomize_options ? { randomize_options: true } : {}),
      };
      await dbCreateSessionWithFreshIds(ids => ({
        ...ids,
        answerKey: session.answerKey,
        numQ: session.numQ,
        numOptions: session.numOptions,
        questions: session.questions,
        courseInstanceId: session.courseInstanceId || null,
        config,
        sessionTitle: `${session.sessionTitle || 'tRAT'} (kopia)`,
      }), user?.id);
      notify(sv ? "Kopia skapad." : "Copy created.");
    } catch (e) {
      notify(friendlyError(e, lang), true);
    }
    onChanged();
  }

  // Avsluta = lägg undan en genomförd tRAT. Den flyttar till Avslutade och
  // kan öppnas igen; radera är bara för sådant som ska bort helt.
  async function handleToggleLock() {
    close();
    const lock = !session.sessionLocked;
    try {
      await dbToggleLock(session.sessionId, lock);
      notify(lock
        ? (sv ? "tRAT:en är avslutad och finns under Avslutade." : "The tRAT is closed and listed under Closed.")
        : (sv ? "tRAT:en är öppen igen." : "The tRAT is open again."));
      onChanged();
    } catch (e) {
      notify(friendlyError(e, lang), true);
    }
  }

  async function handleDelete() {
    close();
    const success = await dbDeleteSession(session.sessionId);
    if (success) {
      notify(t.sessionDeleted);
      onChanged();
    } else {
      notify(sv ? "Kunde inte radera tRAT:en." : "Could not delete the tRAT.", true);
    }
  }

  const itemStyle = { width: "100%", background: "none", border: "none", color: G.text, fontSize: 14, padding: "10px 16px", cursor: "pointer", textAlign: "left" };

  return (
    <div style={{ position: "relative" }} className="no-click">
      <button
        onClick={(e) => { e.stopPropagation(); open ? close() : setOpen(true); }}
        aria-label={sv ? "Fler val" : "More actions"}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ background: "none", border: "none", color: G.muted, fontSize: 20, cursor: "pointer", padding: "4px 8px" }}
      >
        ⋮
      </button>
      {open && (
        <>
          <div
            onClick={(e) => { e.stopPropagation(); close(); }}
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }}
          />
          <div role="menu" style={{
            position: "absolute", right: 0, top: "100%",
            background: G.surface2, border: `1px solid ${G.border}`,
            borderRadius: 12, padding: "8px 0", minWidth: 180, zIndex: 10,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          }}>
            <button role="menuitem" onClick={(e) => { e.stopPropagation(); close(); onView(); }} style={itemStyle}>
              {t.viewEdit}
            </button>
            <button role="menuitem" onClick={(e) => { e.stopPropagation(); handleDuplicate(); }} style={itemStyle}>
              {t.duplicate}
            </button>
            {confirmLock ? (
              <div style={{ padding: "8px 16px" }}>
                <div style={{ fontSize: 12, color: G.text, marginBottom: 8, lineHeight: 1.5 }}>
                  {sv
                    ? "Avsluta tRAT:en? Grupper som är inne skickas till startsidan och frågorna går inte att öppna. Du kan öppna den igen."
                    : "End the tRAT? Groups still in it are sent to the start page and the questions can no longer be opened. You can reopen it."}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={(e) => { e.stopPropagation(); setConfirmLock(false); handleToggleLock(); }}
                    style={{ background: G.gold, border: "none", borderRadius: 6, color: G.onGold, fontSize: 12, fontWeight: 600, padding: "5px 12px", cursor: "pointer" }}>
                    {sv ? "Avsluta" : "End"}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setConfirmLock(false); }}
                    style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 12, padding: "5px 12px", cursor: "pointer" }}>
                    {sv ? "Avbryt" : "Cancel"}
                  </button>
                </div>
              </div>
            ) : (
              <button role="menuitem" onClick={(e) => { e.stopPropagation(); if (session.sessionLocked) handleToggleLock(); else setConfirmLock(true); }} style={itemStyle}>
                {session.sessionLocked ? (sv ? "Öppna igen" : "Reopen") : (sv ? "Avsluta" : "Close")}
              </button>
            )}
            {isOwner && <div style={{ height: 1, background: G.border, margin: "4px 0" }} />}
            {isOwner && (confirmDelete ? (
              /* Inline confirmation — same pattern as "ta bort grupp"
                 in the dashboard, instead of a native confirm() */
              <div style={{ padding: "8px 16px" }}>
                <div style={{ fontSize: 12, color: G.red, marginBottom: 8, lineHeight: 1.5 }}>
                  {sv
                    ? "Radera tRAT:en och alla gruppresultat? Kan inte ångras."
                    : "Delete the tRAT and all group results? Cannot be undone."}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                    style={{ background: G.red, border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 600, padding: "5px 12px", cursor: "pointer" }}>
                    {t.delete}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
                    style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 12, padding: "5px 12px", cursor: "pointer" }}>
                    {sv ? "Avbryt" : "Cancel"}
                  </button>
                </div>
              </div>
            ) : (
              <button role="menuitem"
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
                style={{ ...itemStyle, display: "flex", alignItems: "center", gap: 8, color: G.red }}
              >
                <Trash2 size={14} /> {t.delete}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export { TRATEditor, TeacherSessionList, SessionActionsMenu, useToast, Toast };
